import React, {useState, useEffect, useRef, Suspense, useMemo, useCallback} from 'react';
import Slider from '@components/base/Slider/Slider';
import styles from './Tabs.scss';
import useDockedTabs from "@hooks/useDockedTabs";
import {ScrollToTarget} from "@helpers/HelperFunc";

const ACTIVE_TAB_SWITCH_BUFFER = 200; // px

const Tabs = ({tabs, renderTabsOnScroll = false, color = ''}) => {
	const tabsNavHolderRef = useRef(null);
	const tabsNavRef = useRef(null);
	const tabButtonRefs = useRef({});
	const sentinelRef = useRef(null);
	const sectionRefs = useRef({});
	const sliderRef = useRef(null);
	const swiperWrapperRef = useRef(null);
	const didHandleInitialUrlTabRef = useRef(false);
	const suppressScrollSpyRef = useRef(false);

	const [tabsHeight, setTabsHeight] = useState(0);
	const [indicatorStyle, setIndicatorStyle] = useState(null);

	const shouldRenderTabsOnScroll = renderTabsOnScroll && tabs.length > 1;

	const getDefaultTabIndex = useCallback(() => {
		const idx = tabs.findIndex(tab => tab.default);
		return idx !== -1 ? idx : 0;
	}, [tabs]);

	const getTabFromUrl = useCallback(() => {
		const params = new URLSearchParams(window.location.search);
		const tabId = params.get('tab');

		if (!tabId) {
			return getDefaultTabIndex();
		}

		const foundIndex = tabs.findIndex(tab => tab.id === tabId);
		return foundIndex !== -1 ? foundIndex : getDefaultTabIndex();
	}, [tabs, getDefaultTabIndex]);

	const getValidTabIndexFromUrl = useCallback(() => {
		const params = new URLSearchParams(window.location.search);
		const tabId = params.get('tab');

		if (!tabId) {
			return -1;
		}

		return tabs.findIndex((tab) => tab.id === tabId);
	}, [tabs]);

	const hasTabInUrl = useCallback(() => {
		const params = new URLSearchParams(window.location.search);
		return !!params.get('tab');
	}, []);

	const getRenderedSectionElements = useCallback((maxIndex) => {
		return tabs
		  .slice(0, maxIndex + 1)
		  .map((tab) => sectionRefs.current[tab.id])
		  .filter(Boolean);
	}, [tabs]);

	const suppressScrollSpyTemporarily = useCallback((duration = 1000) => {
		suppressScrollSpyRef.current = true;

		window.clearTimeout(suppressScrollSpyRef.currentTimeoutId);
		suppressScrollSpyRef.currentTimeoutId = window.setTimeout(() => {
			suppressScrollSpyRef.current = false;
		}, duration);
	}, []);

	const initialIndex = useMemo(() => getTabFromUrl(), [getTabFromUrl]);

	const [activeIndex, setActiveIndex] = useState(initialIndex);
	const [loadedUntil, setLoadedUntil] = useState(
	  shouldRenderTabsOnScroll ? initialIndex : tabs.length - 1
	);

	const [shouldSyncUrl, setShouldSyncUrl] = useState(() => hasTabInUrl());

	// Sync state if tabs / mode change dynamically
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		const nextInitialIndex = getTabFromUrl();

		setActiveIndex(nextInitialIndex);
		setLoadedUntil(shouldRenderTabsOnScroll ? nextInitialIndex : tabs.length - 1);
		setShouldSyncUrl(hasTabInUrl());
		didHandleInitialUrlTabRef.current = false;
	}, [tabs, shouldRenderTabsOnScroll, tabs.length, getTabFromUrl, hasTabInUrl]);

	// Cleanup timeout on unmount
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		return () => {
			window.clearTimeout(suppressScrollSpyRef.currentTimeoutId);
		};
	}, []);

	// Active indicator/animation
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	// Move bottom line to current tab
	const updateIndicator = useCallback(() => {
		const activeTab = tabs[activeIndex];
		const wrapper = tabsNavRef.current?.querySelector('.swiper-wrapper') || null;
		const activeButton = activeTab ? tabButtonRefs.current[activeTab.id] : null;

		if (!wrapper || !activeButton) {
			setIndicatorStyle(null);
			return;
		}

		const wrapperRect = wrapper.getBoundingClientRect();
		const buttonRect = activeButton.getBoundingClientRect();

		setIndicatorStyle({
			width: `${buttonRect.width}px`,
			transform: `translateX(${buttonRect.left - wrapperRect.left}px)`
		});
	}, [tabs, activeIndex]);

	useEffect(() => {
		requestAnimationFrame(() => {
			updateIndicator();
		});
	}, [updateIndicator, loadedUntil, tabsHeight]);

	useEffect(() => {
		window.addEventListener('resize', updateIndicator);

		return () => {
			window.removeEventListener('resize', updateIndicator);
		};
	}, [updateIndicator]);

	// On mobile slide to current tab
	useEffect(() => {
		const swiper = sliderRef.current?.swiper;

		if (!swiper) {
			return;
		}

		if (!swiper.slides || !swiper.slides.length) {
			return;
		}

		if (activeIndex < 0 || activeIndex >= swiper.slides.length) {
			return;
		}

		const swiperEl = swiper.el;
		const activeSlideEl = swiper.slides[activeIndex];

		if (!swiperEl || !activeSlideEl) {
			return;
		}

		const swiperRect = swiperEl.getBoundingClientRect();
		const slideRect = activeSlideEl.getBoundingClientRect();

		const visibilityBuffer = 24;

		const isVisibleEnough =
		  slideRect.left >= swiperRect.left + visibilityBuffer &&
		  slideRect.right <= swiperRect.right - visibilityBuffer;

		if (isVisibleEnough) {
			return;
		}

		requestAnimationFrame(() => {
			swiper.slideTo(activeIndex, 300);
		});
	}, [activeIndex, loadedUntil]);

	// Tab activation logic
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	const activateTab = useCallback((index, shouldScroll = true, shouldUpdateUrl = true) => {
		if (index < 0 || index >= tabs.length) {
			return;
		}

		setActiveIndex(index);

		if (shouldUpdateUrl) {
			setShouldSyncUrl(true);
		}

		if (shouldRenderTabsOnScroll) {
			setLoadedUntil((prev) => Math.max(prev, index));
		}

		if (!shouldScroll) {
			return;
		}

		suppressScrollSpyTemporarily();

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const target = sectionRefs.current[tabs[index]?.id];

				if (target) {
					ScrollToTarget(target, {
						multiplier: 1.5
					});
				}
			});
		});
	}, [tabs, shouldRenderTabsOnScroll, suppressScrollSpyTemporarily]);

	// Initial URL tab navigation
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		if (didHandleInitialUrlTabRef.current) {
			return;
		}

		const urlTabIndex = getValidTabIndexFromUrl();

		if (urlTabIndex === -1) {
			didHandleInitialUrlTabRef.current = true;
			return;
		}

		if (shouldRenderTabsOnScroll) {
			setLoadedUntil((prev) => Math.max(prev, urlTabIndex));
		}

		const scrollToInitialTab = () => {
			const target = sectionRefs.current[tabs[urlTabIndex]?.id];

			if (target) {
				didHandleInitialUrlTabRef.current = true;

				suppressScrollSpyTemporarily();

				ScrollToTarget(target, {
					multiplier: 1.5
				});

				return true;
			}

			return false;
		};

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (scrollToInitialTab()) {
					return;
				}

				setTimeout(() => {
					scrollToInitialTab();
				}, 50);
			});
		});
	}, [getValidTabIndexFromUrl, shouldRenderTabsOnScroll, tabs, suppressScrollSpyTemporarily]);

	// Navigate to tab via custom event
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		const handler = (e) => {
			const {id} = e.detail || {};
			const idx = tabs.findIndex((t) => t.id === id);

			if (idx === -1) {
				return;
			}

			activateTab(idx, true, true);
		};

		window.addEventListener('sh:navigate-tab', handler);
		return () => window.removeEventListener('sh:navigate-tab', handler);
	}, [tabs, activateTab]);

	// Add tab name to URL only after explicit tab navigation
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		if (!shouldSyncUrl) {
			return;
		}

		const tab = tabs[activeIndex];
		if (!tab) {
			return;
		}

		const newUrl = `${window.location.pathname}?tab=${encodeURIComponent(tab.id)}`;
		window.history.replaceState(null, '', newUrl);
	}, [activeIndex, tabs, shouldSyncUrl]);

	// Sticky logic
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		if (!tabsNavHolderRef.current) {
			return;
		}

		const measure = () => {
			const rect = tabsNavHolderRef.current.getBoundingClientRect();
			setTabsHeight(rect.height);
		};

		measure();

		const resizeObserver = new ResizeObserver(measure);
		resizeObserver.observe(tabsNavHolderRef.current);

		window.addEventListener('resize', measure);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', measure);
		};
	}, []);

	const {isFixed, offsetTop} = useDockedTabs();

	// Load next tab only when renderTabsOnScroll = true
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		if (!shouldRenderTabsOnScroll) {
			return;
		}
		if (!sentinelRef.current) {
			return;
		}
		if (loadedUntil >= tabs.length - 1) {
			return;
		}

		const observer = new IntersectionObserver(
		  (entries) => {
			  const entry = entries[0];
			  if (!entry?.isIntersecting) {
				  return;
			  }

			  setLoadedUntil((prev) => {
				  if (prev >= tabs.length - 1) {
					  return prev;
				  }
				  return prev + 1;
			  });
		  },
		  {
			  root: null,
			  rootMargin: '300px 0px',
			  threshold: 0.01
		  }
		);

		observer.observe(sentinelRef.current);

		return () => observer.disconnect();
	}, [shouldRenderTabsOnScroll, loadedUntil, tabs.length]);

	// Detect active tab based on scroll position
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		const updateActiveTab = () => {
			if (suppressScrollSpyRef.current) {
				return;
			}

			if (!didHandleInitialUrlTabRef.current && getValidTabIndexFromUrl() !== -1) {
				return;
			}

			const maxIndex = shouldRenderTabsOnScroll ? loadedUntil : tabs.length - 1;
			const sections = getRenderedSectionElements(maxIndex);

			if (!sections.length) {
				return;
			}

			const triggerLine = offsetTop + tabsHeight + 24;
			const switchLine = triggerLine + ACTIVE_TAB_SWITCH_BUFFER;
			let currentIndex = 0;

			sections.forEach((section, index) => {
				const rect = section.getBoundingClientRect();

				if (rect.top <= switchLine) {
					currentIndex = index;
				}
			});

			setActiveIndex((prev) => (prev === currentIndex ? prev : currentIndex));
		};

		updateActiveTab();

		window.addEventListener('scroll', updateActiveTab, {passive: true});
		window.addEventListener('resize', updateActiveTab);

		return () => {
			window.removeEventListener('scroll', updateActiveTab);
			window.removeEventListener('resize', updateActiveTab);
		};
	}, [
		loadedUntil,
		offsetTop,
		tabsHeight,
		shouldRenderTabsOnScroll,
		tabs.length,
		getRenderedSectionElements,
		getValidTabIndexFromUrl
	]);

	// Buttons and content rendering
	//////////////////////////////////////////////////////////////////////////////////////////////////////////

	const tabButtons = tabs.map((tab, index) => (
	  <button
		key={tab.id}
		ref={(el) => {
			if (el) {
				tabButtonRefs.current[tab.id] = el;
			} else {
				delete tabButtonRefs.current[tab.id];
			}
		}}
		onClick={() => activateTab(index, true, true)}
		className={`${styles.tabButton} ${index === activeIndex ? styles.active : ''} ${color ? styles[color] : ''}`}
		type="button">
		  {tab.label}
	  </button>
	));

	const renderedTabs = shouldRenderTabsOnScroll
	  ? tabs.slice(0, loadedUntil + 1)
	  : tabs;

	return (
	  <>
		  {/* No spacer wrapper: position:sticky keeps the tabs in flow, so it must
		      be a direct sibling of the (tall) content for its sticky range to span
		      the whole section. */}
		  <div
			id={'tabsNavHolder'}
			ref={tabsNavHolderRef}
			className={`${styles.tabsNavHolder} ${isFixed ? styles.tabsNavFixed : ''}`}>
			  <div
				ref={tabsNavRef}
				className={styles.tabsNav}>
				  <Slider
					ref={sliderRef}
					modules={[]}
					slidesPerView={'auto'}
					autoplay={false}
					startHintAnimation={false}
					overlay={
						<div
						  className={`${styles.activeIndicator} ${color ? styles[color] : ''}`}
						  style={indicatorStyle || undefined}
						  aria-hidden="true"
						/>
					}>
					  {tabButtons}
				  </Slider>
			  </div>
		  </div>

		  <div className={styles.tabContent}>
			  {renderedTabs.map((tab, index) => {
				  const TabComponent = tab.component;

				  return (
					<section
					  key={tab.id}
					  id={`tab-section-${tab.id}`}
					  data-index={index}
					  ref={(el) => {
						  if (el) {
							  sectionRefs.current[tab.id] = el;
						  } else {
							  delete sectionRefs.current[tab.id];
						  }
					  }}
					  className={styles.tabSection}>
						<Suspense fallback={<div>Зареждане...</div>}>
							<TabComponent/>
						</Suspense>
					</section>
				  );
			  })}

			  {shouldRenderTabsOnScroll && loadedUntil < tabs.length - 1 && (
				<div
				  ref={sentinelRef}
				  className={styles.loadMoreSentinel}
				  aria-hidden="true"
				/>
			  )}
		  </div>
	  </>
	);
};

export default Tabs;